import { useState, useRef, useEffect } from "react";

console.log(
  "[Gantt] Migration SQL à exécuter:\nALTER TABLE projects ADD COLUMN IF NOT EXISTS gantt_data jsonb DEFAULT '[]';"
);

const BLOCK_TYPES = [
  { type: "demolition",    label: "Démolition",    color: "#E57373", duration: 5  },
  { type: "gros-oeuvre",   label: "Gros œuvre",    color: "#FF8A65", duration: 10 },
  { type: "plomberie",     label: "Plomberie",     color: "#4FC3F7", duration: 7  },
  { type: "electricite",   label: "Électricité",   color: "#81C784", duration: 6  },
  { type: "cuisine",       label: "Cuisine",       color: "#FFD54F", duration: 8  },
  { type: "salle-de-bain", label: "Salle de bain", color: "#BA68C8", duration: 7  },
  { type: "peinture",      label: "Peinture",      color: "#4DB6AC", duration: 5  },
  { type: "sol",           label: "Sol",           color: "#A1887F", duration: 4  },
];

const TOTAL_DAYS = 60;
const DAY_W    = 32;
const ROW_H    = 40;
const ROW_GAP  = 6;
const HEADER_H = 48;


export default function GanttChart({ initialBlocks = [], onSave, deadline, readOnly = false, projectName = "projet" }) {
  const [blocks, setBlocks] = useState(initialBlocks);

  const blocksRef   = useRef(blocks);
  const saveRef     = useRef(onSave);
  const dragRef     = useRef(null);
  const blockElRefs = useRef(new Map());

  useEffect(() => {
    if (initialBlocks && initialBlocks.length > 0) {
      setBlocks(initialBlocks);
    }
  }, [initialBlocks]);

  useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  useEffect(() => { saveRef.current   = onSave;  }, [onSave]);

  const addBlock = (bt) => {
    if (readOnly) return;
    const cur = blocksRef.current;
    const occupied = new Set(
      cur.flatMap(b => Array.from({ length: b.duration }, (_, i) => b.startDay + i))
    );
    let startDay = 1;
    while (startDay <= TOTAL_DAYS - bt.duration + 1) {
      if (Array.from({ length: bt.duration }, (_, i) => startDay + i).every(d => !occupied.has(d))) break;
      startDay++;
    }
    if (startDay > TOTAL_DAYS - bt.duration + 1) startDay = 1;
    const newBlocks = [...cur, {
      id: Date.now(), type: bt.type, label: bt.label,
      color: bt.color, startDay, duration: bt.duration,
    }];
    blocksRef.current = newBlocks;
    setBlocks(newBlocks);
    if (!readOnly && onSave) { console.log("[Gantt] onSave called", newBlocks); onSave(newBlocks); }
  };

  const removeBlock = (id) => {
    if (readOnly) return;
    const newBlocks = blocksRef.current.filter(b => b.id !== id);
    blocksRef.current = newBlocks;
    setBlocks(newBlocks);
    if (!readOnly && onSave) { console.log("[Gantt] onSave called", newBlocks); onSave(newBlocks); }
  };

  const startDrag = (e, id, type) => {
    if (readOnly) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const b = blocksRef.current.find(b => b.id === id);
    dragRef.current = {
      id, type,
      startClientX: clientX,
      originalStartDay: b.startDay,
      originalDuration: b.duration,
      lastDd: null,
    };
  };

  useEffect(() => {
    if (readOnly) return;

    const onMove = (e) => {
      if (!dragRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const dd = Math.round((clientX - dragRef.current.startClientX) / DAY_W);
      if (dd === dragRef.current.lastDd) return;
      dragRef.current.lastDd = dd;

      const { id, type, originalStartDay, originalDuration } = dragRef.current;
      const el = blockElRefs.current.get(id);
      if (!el) return;
      const b = blocksRef.current.find(b => b.id === id);
      if (type === "move") {
        const newStart = Math.max(1, Math.min(originalStartDay + dd, TOTAL_DAYS - b.duration + 1));
        el.style.left = ((newStart - 1) * DAY_W + 1) + "px";
      } else {
        const newDur = Math.max(1, Math.min(originalDuration + dd, TOTAL_DAYS - b.startDay + 1));
        el.style.width = (newDur * DAY_W - 4) + "px";
      }
    };

    const onUp = () => {
      if (!dragRef.current) return;
      const { id, type, originalStartDay, originalDuration, lastDd } = dragRef.current;
      dragRef.current = null;
      if (lastDd === null) return;
      const b = blocksRef.current.find(b => b.id === id);
      let next;
      if (type === "move") {
        const newStart = Math.max(1, Math.min(originalStartDay + lastDd, TOTAL_DAYS - b.duration + 1));
        next = blocksRef.current.map(bl => bl.id === id ? { ...bl, startDay: newStart } : bl);
      } else {
        const newDur = Math.max(1, Math.min(originalDuration + lastDd, TOTAL_DAYS - b.startDay + 1));
        next = blocksRef.current.map(bl => bl.id === id ? { ...bl, duration: newDur } : bl);
      }
      blocksRef.current = next;
      setBlocks(next);
      if (!readOnly && saveRef.current) { console.log("[Gantt] onSave called", next); saveRef.current(next); }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend",  onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend",  onUp);
    };
  }, [readOnly]);

  const exportPDF = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    const timeStr = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }).replace(":", "h");

    const weeksHeader = Array.from({ length: 9 }, (_, w) => {
      const pct = (w * 7 / TOTAL_DAYS) * 100;
      return `<div style="position:absolute;left:${pct}%;padding-left:5px;font-size:10px;font-weight:700;color:#8B7355;">S${w + 1}</div>`;
    }).join("");

    const separators = Array.from({ length: 9 }, (_, w) => {
      const pct = (w * 7 / TOTAL_DAYS) * 100;
      return `<div style="position:absolute;left:${pct}%;top:0;bottom:0;border-left:1px solid #e0e0e0;pointer-events:none;"></div>`;
    }).join("");

    const blocksHtml = blocks.map((b, idx) => {
      const left  = ((b.startDay - 1) / TOTAL_DAYS) * 100;
      const width = (b.duration / TOTAL_DAYS) * 100;
      const top   = idx % 2 === 0 ? 40 : 100;
      return `
        <div style="position:absolute;left:${left}%;width:${width}%;top:${top}px;height:36px;
          background:${b.color};border-radius:6px;box-sizing:border-box;padding:5px 6px;overflow:hidden;">
          <div style="font-size:11px;font-weight:700;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${b.label}</div>
          <div style="font-size:9px;color:rgba(255,255,255,0.85);">J${b.startDay}–J${b.startDay + b.duration - 1}</div>
        </div>`;
    }).join("");

    const deadlineHtml = deadline != null && deadline >= 1 && deadline <= TOTAL_DAYS
      ? `<div style="position:absolute;left:${((deadline - 1) / TOTAL_DAYS) * 100}%;top:0;bottom:0;border-left:2px dashed #FF4444;z-index:5;">
           <span style="position:absolute;top:4px;left:4px;font-size:9px;color:#FF4444;background:#fff;padding:1px 4px;border-radius:3px;white-space:nowrap;">J${deadline}</span>
         </div>`
      : "";

    const html = `<!DOCTYPE html>
<html data-theme="light">
<head>
  <meta charset="utf-8">
  <meta name="color-scheme" content="light">
  <title>Planning – ${projectName}</title>
  <style>
    :root { color-scheme: light only; }
    * { color-scheme: light only; }
    html { background: white !important; }
    body { background: white !important; color: black !important; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { size: A4 landscape; margin: 15mm; }
    }
    body { font-family: system-ui, -apple-system, sans-serif; color: #1a1a1a; margin: 0; padding: 20px; }
  </style>
</head>
<body>
  <div style="margin-bottom:16px;">
    <div style="font-size:18px;font-weight:700;color:#1a1a1a;">${projectName}</div>
    <div style="font-size:11px;color:#666;margin-top:4px;">Exporté le ${dateStr} à ${timeStr}</div>
  </div>

  <div style="position:relative;background:#f5f5f5;border-radius:8px;height:200px;width:100%;overflow:hidden;">
    <div style="position:relative;height:28px;border-bottom:1px solid #e0e0e0;">
      ${weeksHeader}
    </div>
    <div style="position:relative;height:172px;">
      ${separators}
      ${blocksHtml}
      ${deadlineHtml}
    </div>
  </div>

  <div style="text-align:center;margin-top:20px;font-size:11px;color:#aaa;">Travea — travea.io</div>
</body>
</html>`;

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  // --- Calcul des lignes (rendu React) ---
  const sorted = [...blocks].sort((a, b) => a.startDay - b.startDay);
  const rowEnds = [];
  const blockRows = new Map();
  for (const b of sorted) {
    let row = rowEnds.findIndex(end => end <= b.startDay);
    if (row === -1) row = rowEnds.length;
    rowEnds[row] = b.startDay + b.duration;
    blockRows.set(b.id, row);
  }
  const numRows = Math.max(rowEnds.length, 1);
  const bodyH = numRows * (ROW_H + ROW_GAP) + 20;

  return (
    <div style={{ position: "relative" }}>
      {!readOnly && (
        <button onClick={exportPDF} style={{ position: "absolute", top: 10, right: 10, zIndex: 20, background: "#252420", border: "1px solid #2A2924", borderRadius: 7, padding: "5px 12px", fontSize: 11, color: "#C9A96E", cursor: "pointer" }}>
          ⬇ Exporter en PDF
        </button>
      )}
      <div style={{ display: "flex", height: "calc(100vh - 200px)", minHeight: 360, background: "#141410", borderRadius: 12, overflow: "hidden", border: "1px solid #2A2924" }}>

        {/* Palette */}
        {!readOnly && (
          <div style={{ width: 124, flexShrink: 0, borderRight: "1px solid #2A2924", padding: "10px 8px", overflowY: "auto", background: "#1A1916" }}>
            <div style={{ fontSize: 9, color: "#5A5450", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 9 }}>AJOUTER</div>
            {BLOCK_TYPES.map(bt => (
              <button key={bt.type} onClick={() => addBlock(bt)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 7, padding: "7px 8px", marginBottom: 5, background: "#252420", border: "1px solid #2A2924", borderRadius: 7, cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: 9, height: 9, borderRadius: 2, background: bt.color, flexShrink: 0 }} />
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 10, color: "#E8E2D8", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{bt.label}</div>
                  <div style={{ fontSize: 9, color: "#5A5450" }}>{bt.duration}j</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Timeline */}
        <div style={{ flex: 1, overflowX: "auto", overflowY: "auto" }}>
          <div style={{ width: TOTAL_DAYS * DAY_W, minWidth: "100%", position: "relative" }}>

            {/* Header */}
            <div style={{ display: "flex", height: HEADER_H, borderBottom: "1px solid #2A2924", position: "sticky", top: 0, background: "#141410", zIndex: 10 }}>
              {Array.from({ length: TOTAL_DAYS }, (_, i) => {
                const day = i + 1;
                const isWeekStart = i % 7 === 0;
                return (
                  <div key={day} style={{ width: DAY_W, flexShrink: 0, borderRight: `1px solid ${isWeekStart ? "#2A2924" : "#1F1E1A"}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", paddingBottom: 5 }}>
                    {isWeekStart && <div style={{ fontSize: 8, color: "#C9A96E", fontWeight: 700, marginBottom: 1 }}>S{Math.ceil(day / 7)}</div>}
                    <div style={{ fontSize: 9, color: isWeekStart ? "#7A7468" : "#3A3830" }}>{day}</div>
                  </div>
                );
              })}
            </div>

            {/* Body */}
            <div style={{ position: "relative", height: bodyH }}>

              {/* Grid lines */}
              {Array.from({ length: TOTAL_DAYS }, (_, i) => (
                <div key={i} style={{ position: "absolute", left: i * DAY_W, top: 0, bottom: 0, width: 1, background: i % 7 === 0 ? "#2A2924" : "#1A1916" }} />
              ))}

              {/* Deadline */}
              {deadline != null && deadline >= 1 && deadline <= TOTAL_DAYS && (
                <div style={{ position: "absolute", left: (deadline - 1) * DAY_W, top: 0, bottom: 0, borderLeft: "2px dashed #FC8181", zIndex: 5, pointerEvents: "none" }}>
                  <div style={{ position: "absolute", top: 5, left: 4, fontSize: 9, color: "#FC8181", background: "#1A0606", borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>
                    J{deadline} · Fin
                  </div>
                </div>
              )}

              {/* Vide */}
              {blocks.length === 0 && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#3A3830", fontSize: 12 }}>
                  {readOnly ? "Aucun bloc planifié" : "Cliquez sur un bloc à gauche pour l'ajouter"}
                </div>
              )}

              {/* Blocks */}
              {blocks.map(b => {
                const row = blockRows.get(b.id) ?? 0;
                return (
                  <div
                    key={b.id}
                    ref={el => { if (el) blockElRefs.current.set(b.id, el); else blockElRefs.current.delete(b.id); }}
                    style={{
                      position: "absolute",
                      left: (b.startDay - 1) * DAY_W + 1,
                      top: row * (ROW_H + ROW_GAP) + 8,
                      width: b.duration * DAY_W - 4,
                      height: ROW_H,
                      background: b.color,
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                      zIndex: 2,
                      userSelect: "none",
                      touchAction: readOnly ? "auto" : "none",
                    }}>
                    <div
                      onMouseDown={readOnly ? undefined : e => startDrag(e, b.id, "move")}
                      onTouchStart={readOnly ? undefined : e => startDrag(e, b.id, "move")}
                      style={{ flex: 1, padding: "0 4px 0 7px", overflow: "hidden", cursor: readOnly ? "default" : "grab" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(0,0,0,0.75)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.label}</div>
                      <div style={{ fontSize: 8, color: "rgba(0,0,0,0.45)" }}>J{b.startDay}–J{b.startDay + b.duration - 1} · {b.duration}j</div>
                    </div>
                    {!readOnly && (
                      <button onClick={() => removeBlock(b.id)}
                        style={{ flexShrink: 0, width: 16, height: 16, borderRadius: "50%", background: "rgba(0,0,0,0.25)", border: "none", cursor: "pointer", fontSize: 8, color: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 2 }}>
                        ✕
                      </button>
                    )}
                    {!readOnly && (
                      <div
                        onMouseDown={e => startDrag(e, b.id, "resize")}
                        onTouchStart={e => startDrag(e, b.id, "resize")}
                        style={{ flexShrink: 0, width: 8, alignSelf: "stretch", cursor: "ew-resize", background: "rgba(0,0,0,0.15)", borderRadius: "0 6px 6px 0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ width: 2, height: 14, background: "rgba(0,0,0,0.3)", borderRadius: 1 }} />
                      </div>
                    )}
                  </div>
                );
              })}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
